import { Fragment, ReactNode, useLayoutEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Checkbox,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import CustomScrollbars from "components/CustomScrollbars";
import useElementWidth from "hooks/useElementWidth";
import useResponsive from "hooks/useResponsive";
import BotsTableScroll, {
  BOTS_BODY_CELL_SX,
  BOTS_HEADER_CELL_SX,
} from "./BotsTableScroll";
import {
  type BotsLayout,
  chooseBotsLayout,
  columnsMinWidth,
  resolveColumns,
} from "./botsTableLayout";

/**
 * One description of a record, two layouts.
 *
 * Above the card breakpoint the descriptors become a `<Table>`; below it they
 * become a list of cards. Both the `<colgroup>` and the cells are built from
 * the same array, so the widths can no longer drift out of step with the
 * columns - which is the bug class the parallel `_XS` width arrays invited.
 *
 * `card` says what the column becomes on a phone:
 *   title   - the card heading (at most one)
 *   badge   - a chip in the row under the heading
 *   field   - a "LABEL  value" row
 *   block   - full-width content (a progress bar)
 *   actions - the card's action row (at most one)
 *   hidden  - dropped; the value is derivable or lives elsewhere on a phone
 */
export type BotsCardSlot =
  | "title"
  | "badge"
  | "field"
  | "block"
  | "actions"
  | "hidden";

export type BotsColumn<T> = {
  key: string;
  header: ReactNode;
  /** Fixed px width for the table layout. Exactly one column may omit it. */
  width?: number;
  align?: "left" | "center";
  cell: (row: T) => ReactNode;
  card?: BotsCardSlot;
  /** Card-specific rendering when the table cell does not suit a card. */
  cardCell?: (row: T) => ReactNode;
  /** Label for a `field` row; defaults to `header` when that is a string. */
  cardLabel?: string;
  /** Width on a laptop (VIEWPORT_COMPACT_MAX_WIDTH); `width` otherwise. */
  compactWidth?: number;
  /** Dropped on a laptop: the value is on the card, in a tooltip or elsewhere. */
  compactHidden?: boolean;
  /** Loading placeholder shaped like the cell, so nothing jumps when rows land. */
  skeleton?: () => ReactNode;
};

export type BotsRecordSelection = {
  selectedIds: (number | string)[];
  onToggle: (id: number | string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  /** Accessible name of the header / card-mode "select all" control. */
  allLabel: string;
  disabled?: boolean;
};

export function botsTableMinWidth<T>(columns: BotsColumn<T>[]): number {
  return columnsMinWidth(columns);
}

export interface BotsRecordTableProps<T> {
  rows: T[];
  columns: BotsColumn<T>[];
  rowKey: (row: T) => number | string;
  /** Defaults to the sum of the column widths - pass one only to override. */
  minWidth?: number;
  fill?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;
  onRowClick?: (row: T) => void;
  selection?: BotsRecordSelection;
  loading?: boolean;
  skeletonRows?: number;
  empty?: ReactNode;
  /** Rendered as a last full-width row / card (e.g. "Add task"). */
  footerRow?: ReactNode;
  /** `layout` says whether the row is a table row or a card right now. */
  renderExpanded?: (row: T, layout: BotsLayout) => ReactNode;
  expandedIds?: (number | string)[];
  onToggleExpand?: (row: T) => void;
  /** Extra element rendered after the expand control (a chevron, a spinner). */
  expandAdornment?: (row: T, expanded: boolean) => ReactNode;
}

function slotOf<T>(column: BotsColumn<T>): BotsCardSlot {
  return column.card ?? "field";
}

/** A "block" column exists for the card layout only - it has no table cell. */
function tableColumns<T>(columns: BotsColumn<T>[]): BotsColumn<T>[] {
  return columns.filter((column) => slotOf(column) !== "block");
}

function cardValue<T>(column: BotsColumn<T>, row: T): ReactNode {
  return (column.cardCell ?? column.cell)(row);
}

function CardFieldRows<T>({
  columns,
  row,
}: {
  columns: BotsColumn<T>[];
  row: T;
}) {
  if (!columns.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        columnGap: 1,
        rowGap: 0,
        mt: 0.25,
      }}
    >
      {columns.map((column) => (
        <Fragment key={column.key}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            {column.cardLabel ??
              (typeof column.header === "string" ? column.header : column.key)}
          </Typography>
          <Box
            sx={{
              minWidth: 0,
              overflowWrap: "anywhere",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              fontSize: "0.8125rem",
            }}
          >
            {cardValue(column, row)}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

export default function BotsRecordTable<T>({
  rows,
  columns,
  rowKey,
  minWidth,
  fill = false,
  bordered = true,
  stickyHeader = true,
  onRowClick,
  selection,
  loading = false,
  skeletonRows = 4,
  empty,
  footerRow,
  renderExpanded,
  expandedIds,
  onToggleExpand,
  expandAdornment,
}: BotsRecordTableProps<T>) {
  const { isCardMode, isCompact } = useResponsive();
  const [measureRef, containerWidth] = useElementWidth();
  const resolved = resolveColumns(columns, { compact: isCompact });
  const shown = tableColumns(resolved);
  const tableMinWidth =
    minWidth ??
    botsTableMinWidth(shown) + (selection ? 48 : 0) + (expandAdornment ? 56 : 0);
  const [layout, setLayout] = useState<BotsLayout>(isCardMode ? "cards" : "table");
  useLayoutEffect(() => {
    setLayout((previous) =>
      chooseBotsLayout({
        cardMode: isCardMode,
        containerWidth,
        minWidth: tableMinWidth,
        previous,
      }),
    );
  }, [isCardMode, containerWidth, tableMinWidth]);
  const expanded = (row: T) =>
    (expandedIds ?? []).includes(rowKey(row)) && renderExpanded != null;

  if (layout === "cards") {
    const titleColumn = columns.find((c) => slotOf(c) === "title");
    const badgeColumns = columns.filter((c) => slotOf(c) === "badge");
    const fieldColumns = columns.filter((c) => slotOf(c) === "field");
    const blockColumns = columns.filter((c) => slotOf(c) === "block");
    const actionsColumn = columns.find((c) => slotOf(c) === "actions");
    const allSelected =
      selection != null &&
      rows.length > 0 &&
      rows.every((row) => selection.selectedIds.includes(rowKey(row)));
    const someSelected =
      selection != null &&
      rows.some((row) => selection.selectedIds.includes(rowKey(row)));

    const cards = (
      <Box
        ref={measureRef}
        sx={{ minWidth: 0, width: "100%", display: "flex", flexDirection: "column", gap: 1 }}
      >
        {selection != null && rows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minHeight: 36,
              px: 0.5,
            }}
          >
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              disabled={selection.disabled}
              onChange={(e) => selection.onToggleAll(e.target.checked)}
              inputProps={{ "aria-label": selection.allLabel }}
            />
            <Typography variant="body2" color="text.secondary">
              {selection.selectedIds.length > 0
                ? `${selection.selectedIds.length} selected`
                : "Select all"}
            </Typography>
          </Box>
        )}
        <Stack
          divider={<Divider />}
          sx={{
            border: bordered ? 1 : 0,
            borderColor: "divider",
            borderRadius: bordered ? 1 : 0,
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {loading
            ? Array.from({ length: skeletonRows }).map((_, index) => (
                <Box key={index} sx={{ p: 1.5 }}>
                  <Skeleton variant="text" width="65%" />
                  <Skeleton variant="text" width="45%" />
                  <Skeleton variant="text" width="55%" />
                </Box>
              ))
            : rows.map((row) => {
                const id = rowKey(row);
                const isExpanded = expanded(row);
                const openRow = () => {
                  if (onToggleExpand) onToggleExpand(row);
                  else onRowClick?.(row);
                };
                return (
                  <Box key={id} sx={{ px: 1.25, py: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      {selection != null && (
                        <Checkbox
                          size="small"
                          sx={{ p: 0.5, mt: -0.25 }}
                          checked={selection.selectedIds.includes(id)}
                          disabled={selection.disabled}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            selection.onToggle(id, e.target.checked)
                          }
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {titleColumn != null &&
                          (onRowClick || onToggleExpand ? (
                            <ButtonBase
                              onClick={openRow}
                              sx={{
                                justifyContent: "flex-start",
                                textAlign: "left",
                                width: "100%",
                                minHeight: 28,
                                borderRadius: 0.5,
                                fontWeight: 700,
                              }}
                            >
                              <Box sx={{ minWidth: 0, width: "100%" }}>
                                {cardValue(titleColumn, row)}
                              </Box>
                            </ButtonBase>
                          ) : (
                            <Box sx={{ minWidth: 0, fontWeight: 700 }}>
                              {cardValue(titleColumn, row)}
                            </Box>
                          ))}
                        {badgeColumns.length > 0 && (
                          <Stack
                            direction="row"
                            sx={{
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.25,
                              minWidth: 0,
                            }}
                          >
                            {badgeColumns.map((column) => (
                              <Box key={column.key} sx={{ minWidth: 0 }}>
                                {cardValue(column, row)}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                      {expandAdornment?.(row, isExpanded)}
                    </Box>
                    <CardFieldRows columns={fieldColumns} row={row} />
                    {blockColumns.map((column) => (
                      <Box key={column.key} sx={{ mt: 0.75, minWidth: 0 }}>
                        {cardValue(column, row)}
                      </Box>
                    ))}
                    {actionsColumn != null && (
                      <Box
                        sx={{
                          mt: 0.5,
                          minWidth: 0,
                          display: "flex",
                          justifyContent: "flex-end",
                          "& .MuiButton-root": { minHeight: 36 },
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cardValue(actionsColumn, row)}
                      </Box>
                    )}
                    {isExpanded && (
                      <Box sx={{ mt: 1.5, minWidth: 0 }}>
                        {renderExpanded?.(row, "cards")}
                      </Box>
                    )}
                  </Box>
                );
              })}
          {!loading && rows.length === 0 && empty != null && (
            <Box sx={{ p: 2.5, textAlign: "center" }}>{empty}</Box>
          )}
          {footerRow != null && <Box sx={{ p: 1.5 }}>{footerRow}</Box>}
        </Stack>
      </Box>
    );
    // A desktop panel that ran out of width keeps its own scroller; a phone
    // has exactly one, the page's.
    if (fill && !isCardMode) {
      return (
        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex" }}>
          <CustomScrollbars hideNative="vertical" trackGutter>
            {cards}
          </CustomScrollbars>
        </Box>
      );
    }
    return cards;
  }

  const columnCount =
    shown.length + (selection != null ? 1 : 0) + (expandAdornment ? 1 : 0);
  return (
    <BotsTableScroll fill={fill} bordered={bordered} measureRef={measureRef}>
      <Table
        size="small"
        stickyHeader={stickyHeader}
        sx={{ tableLayout: "fixed", minWidth: tableMinWidth }}
      >
        <colgroup>
          {selection != null && <col style={{ width: 48 }} />}
          {shown.map((column) => (
            <col
              key={column.key}
              style={column.width != null ? { width: column.width } : undefined}
            />
          ))}
          {expandAdornment && <col style={{ width: 56 }} />}
        </colgroup>
        <TableHead>
          <TableRow>
            {selection != null && (
              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX}>
                <Checkbox
                  size="small"
                  disabled={selection.disabled || rows.length === 0}
                  checked={
                    rows.length > 0 &&
                    rows.every((row) =>
                      selection.selectedIds.includes(rowKey(row)),
                    )
                  }
                  indeterminate={
                    rows.some((row) =>
                      selection.selectedIds.includes(rowKey(row)),
                    ) &&
                    !rows.every((row) =>
                      selection.selectedIds.includes(rowKey(row)),
                    )
                  }
                  onChange={(e) => selection.onToggleAll(e.target.checked)}
                  inputProps={{ "aria-label": selection.allLabel }}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
            )}
            {shown.map((column) => (
              <TableCell
                key={column.key}
                sx={{
                  ...BOTS_HEADER_CELL_SX,
                  ...(column.align === "left" ? { textAlign: "left" } : {}),
                }}
              >
                {column.header}
              </TableCell>
            ))}
            {expandAdornment && (
              <TableCell padding="checkbox" sx={BOTS_HEADER_CELL_SX} />
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: skeletonRows }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {selection != null && <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX} />}
                {shown.map((column) => (
                  <TableCell key={column.key} sx={BOTS_BODY_CELL_SX}>
                    {column.skeleton ? column.skeleton() : <Skeleton variant="text" />}
                  </TableCell>
                ))}
                {expandAdornment && <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX} />}
              </TableRow>
            ))}
          {!loading &&
            rows.map((row) => {
              const id = rowKey(row);
              const isExpanded = expanded(row);
              return (
                <Fragment key={id}>
                  <TableRow
                    hover
                    selected={isExpanded}
                    onClick={
                      onToggleExpand
                        ? () => onToggleExpand(row)
                        : onRowClick
                          ? () => onRowClick(row)
                          : undefined
                    }
                    sx={{
                      cursor: onRowClick || onToggleExpand ? "pointer" : "default",
                      ...(isExpanded ? { "& > td": { borderBottom: 0 } } : {}),
                    }}
                  >
                    {selection != null && (
                      <TableCell
                        padding="checkbox"
                        sx={BOTS_BODY_CELL_SX}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selection.selectedIds.includes(id)}
                          disabled={selection.disabled}
                          onChange={(e) => selection.onToggle(id, e.target.checked)}
                          sx={{ p: 0.5 }}
                        />
                      </TableCell>
                    )}
                    {shown.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          ...BOTS_BODY_CELL_SX,
                          ...(column.align === "left"
                            ? { textAlign: "left" }
                            : {}),
                        }}
                        onClick={
                          slotOf(column) === "actions"
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                    {expandAdornment && (
                      <TableCell padding="checkbox" sx={BOTS_BODY_CELL_SX}>
                        {expandAdornment(row, isExpanded)}
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        sx={{
                          p: 0,
                          borderBottom: 1,
                          borderColor: "divider",
                        }}
                      >
                        {renderExpanded?.(row, "table")}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          {!loading && rows.length === 0 && empty != null && (
            <TableRow>
              <TableCell colSpan={columnCount} sx={{ textAlign: "center", py: 3 }}>
                {empty}
              </TableCell>
            </TableRow>
          )}
          {footerRow != null && (
            <TableRow>
              <TableCell
                colSpan={columnCount}
                sx={{
                  borderTop: "1px dashed",
                  borderTopColor: "divider",
                  py: 0.25,
                }}
              >
                {footerRow}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </BotsTableScroll>
  );
}
